import React from 'react';
import classes from './ReportsTableHeader.module.css';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import { DateSearchV2 } from '../../UI/Inputs/DateSearchV2';
import searchIcon from '../../../assets/icons/search.svg';

import {
  ExportToExcel,
  ICustomerExport,
  IDiagnosticReportsExport,
  ISalesOrderExport,
  IVideoConsultingReportsExport,
} from '../../UI/ExportToExcel/ExportToExcel';
import PrimaryButtonV2 from '../../UI/Buttons/PrimaryButtonV2';
import { deviceType } from '../../../utils/miscUtils';
import downloadIcon from '../../../assets/icons/download-icon.svg';
import { DropdownMenu } from '../../UI/DropdownMenu/DropdownMenu';
import { CheckBox } from '../../UI/CheckBox/CheckBox';
import closeIcon from '../../../assets/icons/close.svg';
import { Filters } from './Filters';
import { useTranslation } from '../../../hooks/useTranslations';

const ReportsTableHeader: React.FC<{
  tabs: { displayName: string; value: number; icon?: any }[];
  isDropdown?: boolean;
  firstNameSearch: string;
  setFirstNameSearch: (val: string) => void;
  searchPlaceholder?: string;
  dateBegin?: Date;
  setDateBegin?: (val: Date | undefined) => void;
  dateEnd?: Date;
  setDateEnd?: (val: Date | undefined) => void;
  setDateColumn?: (value: 'booking_time' | 'appointment_time') => void;
  selectedDateColumn?: string;
  maxAppointmentDate?: Date;
  selectedSalesOrder?: SalesOrderInfo;
  selectedTab?: number;
  setSelectedTab?: (tab: number) => void;
  onClose?: () => void;
  dataToExport?:
    | ISalesOrderExport[]
    | IDiagnosticReportsExport[]
    | IVideoConsultingReportsExport[]
    | ICustomerExport[];
  showExportButton?: boolean;
  onBookAnotherTest?: () => void;
  showDownloadButton?: boolean;
  handleDownload?: () => void;
  filterByService?: number;
  setFilterByService?: (value?: number) => void;
  filterByStatus?: number;
  setFilterByStatus?: (value?: number) => void;
  serviceTypes?: { service_id: number; service_name: string }[];
  statusTypes?: { id: number; label: string }[];
  registerOnClick?: () => void;
}> = ({
  tabs,
  isDropdown,
  firstNameSearch,
  setFirstNameSearch,
  searchPlaceholder,
  dateBegin,
  setDateBegin,
  dateEnd,
  setDateEnd,
  setDateColumn,
  selectedDateColumn,
  maxAppointmentDate,
  selectedSalesOrder,
  selectedTab,
  setSelectedTab,
  onClose,
  dataToExport,
  showExportButton,
  onBookAnotherTest,
  handleDownload,
  showDownloadButton,
  setFilterByService,
  filterByStatus,
  setFilterByStatus,
  statusTypes,
  registerOnClick,
  // isAddingPerson,
  // setIsAddingPerson,
  // hasRegisterButton,
}) => {
  const isTablet = deviceType() === 'portrait' || deviceType() === 'landscape';
  const { translations } = useTranslation();

  return (
    <div className={classes.container}>
      {selectedSalesOrder ? (
        <div className={classes.tabsContainer}>
          {isDropdown ? (
            <div style={{ padding: '15px' }}>
              <DropdownMenu
                items={tabs.map((tab) => ({
                  label: tab.displayName,
                  icon: tab.icon,
                  value: tab.value,
                }))}
                selectedItemValue={selectedTab}
                onSelectItem={setSelectedTab}
              />
            </div>
          ) : (
            <div className={classes.tabs}>
              {tabs.map((tab, index) => (
                <div
                  key={index}
                  className={`
                    ${classes.tab}
                    ${selectedTab === tab.value ? classes.selectedTab : ''}
                    ${tab.icon ? '' : classes.uppercaseTab}
                  `}
                  onClick={() => setSelectedTab && setSelectedTab(tab.value)}
                >
                  {tab.icon && <img className={classes.tabIcon} src={tab.icon} alt={'tab-icon'} />}
                  {tab.displayName}
                </div>
              ))}
            </div>
          )}
          {typeof onBookAnotherTest === 'function' && (
            <PrimaryButtonV2
              text={translations('book.another.test')}
              containerStyle={{ marginLeft: 'auto', marginRight: 15 }}
              buttonStyle={{ height: 40, padding: '0 20px', minWidth: isTablet ? 125 : 150 }}
              onClick={onBookAnotherTest}
            />
          )}
          <div className={classes.actions}>
            {showDownloadButton ? (
              <div onClick={handleDownload} className={classes.downloadContainer}>
                <div className={classes.downloadButton}>
                  <span>{translations('download')}</span>
                  <img className={classes.downloadIcon} src={downloadIcon} alt={'download-icon'} />
                </div>
              </div>
            ) : null}
            {onClose && (
              <img
                className={classes.endIcon}
                src={closeIcon}
                alt={'close icon'}
                onClick={() => onClose()}
              />
            )}
          </div>
        </div>
      ) : (
        <div className={classes.inputFieldsContainer}>
          <div className={classes.searchByName}>
            <input
              placeholder={translations(searchPlaceholder ?? 'search.by.name').toString()}
              type={'text'}
              className={classes.textInputField}
              value={firstNameSearch}
              onChange={(event) => setFirstNameSearch(event.target.value)}
            />
            <img src={searchIcon} alt={'Search'} className={classes.searchByNameIcon} />
          </div>
          {setDateEnd && setDateBegin && (
            <>
              {setDateColumn && (
                <>
                  <CheckBox
                    isChecked={selectedDateColumn === 'booking_time'}
                    onChange={(isChecked) => {
                      if (isChecked) {
                        setDateColumn('booking_time');
                      }
                    }}
                    label={translations('booking_time').toString()}
                  />
                  <div style={{ marginLeft: 20 }} />
                  <CheckBox
                    isChecked={selectedDateColumn === 'appointment_time'}
                    onChange={(isChecked) => {
                      if (isChecked) {
                        setDateColumn('appointment_time');
                      }
                    }}
                    label={translations('appointment_time').toString()}
                  />
                </>
              )}
              <DateSearchV2
                startDateLabel={translations('start')}
                endDateLabel={translations('end')}
                placeholder={translations('select.date').toString()}
                startDateOnChange={setDateBegin}
                endDateOnChange={setDateEnd}
                startDateValue={dateBegin}
                endDateValue={dateEnd}
                selectedColumn={selectedDateColumn}
                maxAppointmentDate={maxAppointmentDate}
              />
              {(setFilterByService || setFilterByStatus) && (
                <Filters
                  filterByStatus={filterByStatus}
                  setFilterByStatus={setFilterByStatus}
                  // filterByService={filterByService}
                  // setFilterByService={setFilterByService}
                  // serviceTypes={serviceTypes}
                  statusTypes={statusTypes}
                />
              )}
            </>
          )}
        </div>
      )}
      {registerOnClick && (
        <PrimaryButtonV2
          text={translations('button.register')}
          containerStyle={{}}
          buttonStyle={{
            height: 44,
            borderRadius: 7,
          }}
          disabled={false}
          onClick={() => {
            registerOnClick();
          }}
        />
      )}
      {dataToExport && showExportButton ? (
        <ExportToExcel
          apiData={dataToExport}
          fileName={`healthpoint_attachment_${new Date().getTime()}`}
        />
      ) : null}
    </div>
  );
};

export { ReportsTableHeader };
